import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

const DEFAULT_PERSONALITY_LABELS = {
  friendly: { label: 'Friendly', color: 'bg-green-100 text-green-700' },
  tough: { label: 'Tough', color: 'bg-red-100 text-red-700' },
  tricky: { label: 'Tricky', color: 'bg-purple-100 text-purple-700' },
  generous: { label: 'Generous', color: 'bg-blue-100 text-blue-700' },
  impatient: { label: 'Impatient', color: 'bg-amber-100 text-amber-700' },
};

const DEFAULT_PERSONALITY_TIPS = {
  friendly: 'Easy to bargain with!',
  tough: 'Hard bargainer, be careful',
  tricky: 'Prices seem low but watch out',
  generous: 'Great deals if you bargain',
  impatient: 'Be quick, mood drops fast',
};

const CALLOUT_PHRASES = [
  { darija: 'Aji, aji! 3endi 7wayej zwinin!', english: 'Come, come! I have nice clothes!' },
  { darija: 'Shuf hna! Ataman mezyanin!', english: 'Look here! Good prices!' },
  { darija: 'Ya salaam, dkhol shuf!', english: 'Wow, come in and look!' },
  { darija: 'Marhba bik! Shri m3aya!', english: 'Welcome! Shop with me!' },
  { darija: 'A khouya, 3endi ga3 ma bghiti!', english: 'Brother, I have everything you want!' },
  { darija: '7wayej jdad! Tssana shuf!', english: 'New clothes! Wait, look!' },
];

const AWNING_COLORS = [
  'from-[#C84B31] via-[#D4A843] to-[#2D6A4F]',
  'from-[#2D6A4F] via-[#C84B31] to-[#D4A843]',
  'from-[#D4A843] via-[#2D6A4F] to-[#C84B31]',
  'from-[#8B4513] via-[#D4A843] to-[#C84B31]',
  'from-[#C84B31] via-[#2D6A4F] to-[#D4A843]',
  'from-[#2D6A4F] via-[#D4A843] to-[#8B4513]',
];

/* ─── Sub-components ─── */

function ArrowButton({ direction, disabled, onClick }) {
  const isUp = direction === 'up';
  return (
    <div className="flex justify-center py-0.5">
      <button
        onClick={onClick}
        disabled={disabled}
        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center
          transition-all shadow-sm border-2
          ${disabled
            ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-default'
            : `bg-[#2D6A4F] text-white border-[#2D6A4F] hover:bg-[#40916C] hover:border-[#40916C] active:scale-90 cursor-pointer ${isUp ? 'animate-arrow-up' : 'animate-arrow-down'}`
          }`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round"
                d={isUp ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
        </svg>
      </button>
    </div>
  );
}

function PathDots({ active }) {
  return (
    <div className="flex flex-col items-center gap-1.5 py-4">
      {[0, 1, 2, 3, 4].map(d => (
        <div
          key={d}
          className={`w-1.5 h-1.5 rounded-full transition-colors duration-500 ${
            active ? 'bg-amber-400/50' : 'bg-amber-300/25'
          }`}
        />
      ))}
    </div>
  );
}

function EntranceGate({ merchantCount, merchantAvatars, onEnter, gameSettings = {} }) {
  const gateIcon = gameSettings.fm_gate_icon || '🏮';
  const gateTitle = gameSettings.fm_gate_title || 'Bab el Souk';
  const gateSubtitle = (gameSettings.fm_gate_subtitle || '{count} merchants await you').replace('{count}', merchantCount);
  const enterText = gameSettings.fm_enter_button || 'Enter Souk';

  return (
    <div className="max-w-md mx-auto text-center py-6 sm:py-8 animate-gate">
      {/* Decorative arch */}
      <div className="bg-gradient-to-b from-[#C84B31]/10 to-transparent rounded-t-[50px] sm:rounded-t-[60px] border-2 border-b-0 border-[#C84B31]/30 px-5 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #C84B31 0, #C84B31 1px, transparent 0, transparent 12px)'
        }} />
        <span className="text-4xl sm:text-5xl block relative">{gateIcon}</span>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-2 relative">{gateTitle}</h2>
        <p className="text-sm text-gray-500 mt-1 relative">{gateSubtitle}</p>
        <div className="flex justify-center gap-2 sm:gap-3 mt-4 relative">
          {merchantAvatars.map((avatar, i) => (
            <div key={i} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-amber-100 to-orange-50 border-2 border-amber-300 flex items-center justify-center shadow-sm"
                 style={{ animationDelay: `${i * 0.1}s` }}>
              {avatar.startsWith?.('http') ? (
                <img src={avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-lg sm:text-xl">{avatar}</span>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="border-2 border-t-0 border-[#C84B31]/30 rounded-b-lg px-5 sm:px-8 pb-5 sm:pb-6 pt-3 bg-gradient-to-b from-transparent to-[#C84B31]/5">
        <div className="flex justify-center gap-1 mb-3">
          <div className="w-1.5 h-6 bg-[#C84B31]/20 rounded-full" />
          <div className="w-1.5 h-6 bg-[#C84B31]/15 rounded-full" />
          <div className="w-1.5 h-6 bg-[#C84B31]/20 rounded-full" />
          <div className="w-1.5 h-6 bg-[#C84B31]/15 rounded-full" />
          <div className="w-1.5 h-6 bg-[#C84B31]/20 rounded-full" />
        </div>
        <button
          onClick={onEnter}
          className="px-8 py-3 sm:px-10 sm:py-3.5 rounded-xl bg-[#2D6A4F] text-white font-bold text-base sm:text-lg shadow-lg hover:bg-[#40916C] active:scale-95 transition-all animate-walk-pulse"
        >
          🚪 {enterText}
        </button>
      </div>
    </div>
  );
}

function ExitGate({ gameSettings = {} }) {
  return (
    <div className="max-w-sm mx-auto text-center py-6 animate-fade-up">
      <div className="border-2 border-dashed border-[#C84B31]/25 rounded-2xl px-6 py-5 bg-[#C84B31]/5">
        <span className="text-3xl block mb-2">{gameSettings.fm_gate_icon || '🏮'}</span>
        <p className="text-sm font-bold text-gray-600">{gameSettings.fm_exit_title || 'End of Souk'}</p>
        <p className="text-xs text-gray-400 mt-1">{gameSettings.fm_exit_subtitle || "You've seen all the merchants!"}</p>
      </div>
    </div>
  );
}

function FriendshipHearts({ level }) {
  const hearts = Math.ceil((level || 0) / 2);
  return (
    <div className="flex gap-px">
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={`text-[8px] sm:text-[9px] ${i < hearts ? 'opacity-100' : 'opacity-25'}`}>
          {i < hearts ? '❤️' : '🤍'}
        </span>
      ))}
    </div>
  );
}

function MerchantStall({ merchant, callout, itemCount, isLeft, index, isActive, friendship, onClick, gameSettings = {} }) {
  const labels = gameSettings.fm_personality_labels || {};
  const tips = gameSettings.fm_personality_tips || {};
  const personality = labels[merchant.personality] || DEFAULT_PERSONALITY_LABELS[merchant.personality] || DEFAULT_PERSONALITY_LABELS.friendly;
  const tip = tips[merchant.personality] || DEFAULT_PERSONALITY_TIPS[merchant.personality];
  const img = merchant.image_url;
  const isEmoji = !img?.startsWith('http');
  const awning = AWNING_COLORS[index % AWNING_COLORS.length];
  const visited = (friendship || 0) > 0;

  return (
    <button
      onClick={isActive ? onClick : undefined}
      disabled={!isActive}
      className={`w-full bg-white rounded-xl overflow-hidden text-left transition-all duration-500
        ${isActive
          ? 'shadow-md border-2 border-[#D4A843] animate-merchant-activate cursor-pointer hover:shadow-lg active:scale-[0.97]'
          : 'shadow-sm border border-gray-200 cursor-default'
        }`}
    >
      <div className={`h-1.5 sm:h-2 bg-gradient-to-r ${awning}`} />
      <div className="p-2 sm:p-2.5">
        <div className={`flex items-center gap-1.5 sm:gap-2 ${isActive ? 'mb-1.5' : 'mb-0'} ${isLeft ? '' : 'flex-row-reverse'}`}>
          <div className="relative flex-shrink-0">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-amber-100 to-orange-50 border-2 border-amber-300 flex items-center justify-center shadow-sm ${isActive ? 'animate-merchant-wave' : ''}`}>
              {isEmoji ? (
                <span className="text-base sm:text-lg">{img || '🧔'}</span>
              ) : (
                <img src={img} alt="" className="w-full h-full rounded-full object-cover" />
              )}
            </div>
            {visited && (
              <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-[8px] sm:text-[9px] shadow-sm">✓</span>
            )}
          </div>
          <div className={`flex-1 min-w-0 ${isLeft ? 'text-left' : 'text-right'}`}>
            <p className="font-bold text-gray-900 text-xs sm:text-sm leading-tight truncate">
              {merchant.name || merchant.name_english}
            </p>
            {merchant.specialty && (
              <p className="text-[9px] sm:text-[10px] text-amber-600 font-medium truncate mt-0.5">{merchant.specialty}</p>
            )}
            <div className={`flex items-center gap-1 mt-0.5 ${isLeft ? '' : 'justify-end'}`}>
              <span className={`text-[8px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded-full ${personality.color}`}>
                {personality.label}
              </span>
              <span className="text-[9px] sm:text-[10px] text-gray-400">{itemCount} items</span>
            </div>
            {visited && (
              <div className={`mt-0.5 ${isLeft ? '' : 'flex justify-end'}`}>
                <FriendshipHearts level={friendship} />
              </div>
            )}
          </div>
        </div>
        {isActive && callout && (
          <div className={`bg-[#F5F0E8] rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 animate-callout ${isLeft ? 'rounded-bl-sm' : 'rounded-br-sm'}`}>
            <p className="text-[10px] sm:text-xs lg:text-sm text-gray-700 font-medium leading-tight">"{callout.darija}"</p>
            <p className="text-[9px] sm:text-[10px] lg:text-xs text-gray-400 mt-0.5 leading-tight">{callout.english}</p>
          </div>
        )}
        {isActive && tip && (
          <p className={`text-[8px] sm:text-[9px] text-gray-400 italic mt-1.5 ${isLeft ? 'text-left' : 'text-right'}`}>
            💡 {tip}
          </p>
        )}
      </div>
    </button>
  );
}

/* ─── Souk Map Strip — visual alley map at top ─── */

function SoukMapStrip({ rows, currentRow, merchantFriendship, avatarImgUrl, playerAvatar, onJumpTo, isWalking, gameSettings }) {
  const stripRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current && stripRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [currentRow]);

  return (
    <div className="bg-gradient-to-r from-amber-50/80 via-white/90 to-amber-50/80 backdrop-blur-sm border-b border-amber-200/50 shadow-sm">
      <div className="max-w-2xl mx-auto px-2 py-2.5">
        <div className="flex items-center justify-between mb-1.5 px-1">
          <span className="text-[9px] sm:text-[10px] font-bold text-amber-700 uppercase tracking-wider">Souk Map</span>
          <span className="text-[9px] sm:text-[10px] text-gray-400">
            {currentRow === -1 ? 'Entrance' : `Stop ${currentRow + 1}/${rows.length}`}
          </span>
        </div>
        <div ref={stripRef} className="flex items-start overflow-x-auto py-1 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {/* Entrance gate */}
          <button
            ref={currentRow === -1 ? activeRef : null}
            onClick={() => !isWalking && onJumpTo(-1)}
            className={`flex flex-col items-center flex-shrink-0 transition-all ${currentRow === -1 ? '' : 'opacity-50 hover:opacity-80'}`}
            style={{ minWidth: 44 }}
          >
            <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center transition-all ${
              currentRow === -1
                ? 'bg-[#C84B31]/15 border-2 border-[#C84B31]/40 shadow-sm'
                : 'bg-[#C84B31]/5 border border-[#C84B31]/20'
            }`}>
              <span className="text-sm sm:text-base">{gameSettings.fm_gate_icon || '🏮'}</span>
            </div>
            {currentRow === -1 && (
              <div className="flex flex-col items-center mt-1 animate-fade-up">
                <div className="w-6 h-6 rounded-full border-2 border-[#D4A843] bg-white overflow-hidden flex items-center justify-center shadow-sm">
                  {avatarImgUrl
                    ? <img src={avatarImgUrl} alt="" className="w-full h-full object-cover" />
                    : <span className="text-[9px]">{playerAvatar?.icon || '🧑'}</span>}
                </div>
                <span className="text-[7px] sm:text-[8px] font-bold text-[#2D6A4F] mt-0.5">YOU</span>
              </div>
            )}
            {currentRow !== -1 && (
              <span className="text-[7px] text-gray-400 mt-0.5">Gate</span>
            )}
          </button>

          {/* Merchant stops */}
          {rows.map((row, i) => {
            const isHere = currentRow === i;
            const leftVisited = (merchantFriendship[row.left?.id] || 0) > 0;
            const rightVisited = row.right ? (merchantFriendship[row.right?.id] || 0) > 0 : false;
            const bothVisited = leftVisited && (rightVisited || !row.right);
            const leftImg = row.left?.image_url;
            const rightImg = row.right?.image_url;
            const leftIsEmoji = !leftImg?.startsWith?.('http');
            const rightIsEmoji = !rightImg?.startsWith?.('http');

            return (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center justify-center flex-shrink-0 pt-3" style={{ minWidth: 20 }}>
                  <div className={`h-[3px] w-full rounded-full transition-colors duration-300 ${
                    i <= currentRow ? 'bg-[#2D6A4F]/60' : 'bg-amber-200/80'
                  }`} />
                </div>
                <button
                  ref={isHere ? activeRef : null}
                  onClick={() => !isWalking && onJumpTo(i)}
                  className={`flex flex-col items-center flex-shrink-0 transition-all ${
                    isHere ? '' : 'opacity-60 hover:opacity-90'
                  }`}
                  style={{ minWidth: 52 }}
                >
                  <div className="flex items-center gap-0.5">
                    <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md flex items-center justify-center overflow-hidden transition-all ${
                      isHere ? 'border-2 border-[#D4A843] shadow-sm bg-amber-50'
                        : leftVisited ? 'border border-green-300 bg-green-50'
                          : 'border border-amber-200 bg-amber-50/50'
                    }`}>
                      {leftIsEmoji
                        ? <span className="text-[9px] sm:text-[10px]">{leftImg || '🧔'}</span>
                        : <img src={leftImg} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className={`w-1 h-1 rounded-full flex-shrink-0 ${isHere ? 'bg-[#D4A843]' : 'bg-amber-200'}`} />
                    {row.right ? (
                      <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md flex items-center justify-center overflow-hidden transition-all ${
                        isHere ? 'border-2 border-[#D4A843] shadow-sm bg-amber-50'
                          : rightVisited ? 'border border-green-300 bg-green-50'
                            : 'border border-amber-200 bg-amber-50/50'
                      }`}>
                        {rightIsEmoji
                          ? <span className="text-[9px] sm:text-[10px]">{rightImg || '🧔'}</span>
                          : <img src={rightImg} alt="" className="w-full h-full object-cover" />}
                      </div>
                    ) : (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md border border-dashed border-amber-200/50 flex items-center justify-center">
                        <span className="text-[8px] opacity-30">🏪</span>
                      </div>
                    )}
                  </div>
                  {isHere && (
                    <div className="flex flex-col items-center mt-1 animate-fade-up">
                      <div className="w-6 h-6 rounded-full border-2 border-[#D4A843] bg-white overflow-hidden flex items-center justify-center shadow-sm">
                        {avatarImgUrl
                          ? <img src={avatarImgUrl} alt="" className="w-full h-full object-cover" />
                          : <span className="text-[9px]">{playerAvatar?.icon || '🧑'}</span>}
                      </div>
                      <span className="text-[7px] sm:text-[8px] font-bold text-[#2D6A4F] mt-0.5">YOU</span>
                    </div>
                  )}
                  {!isHere && bothVisited && <span className="text-[8px] text-green-500 mt-1">✓</span>}
                  {!isHere && !bothVisited && (
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${i < currentRow ? 'bg-[#2D6A4F]/30' : 'bg-amber-200'}`} />
                  )}
                </button>
              </React.Fragment>
            );
          })}

          <div className="flex flex-col items-center justify-center flex-shrink-0 pt-3" style={{ minWidth: 20 }}>
            <div className={`h-[3px] w-full rounded-full ${currentRow >= rows.length - 1 ? 'bg-[#2D6A4F]/60' : 'bg-amber-200/80'}`} />
          </div>
          <div className="flex flex-col items-center flex-shrink-0 opacity-40" style={{ minWidth: 44 }}>
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#C84B31]/5 border border-[#C84B31]/15 flex items-center justify-center">
              <span className="text-sm sm:text-base">{gameSettings.fm_gate_icon || '🏮'}</span>
            </div>
            <span className="text-[7px] text-gray-400 mt-0.5">Exit</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Inline Entrance — shown when avatar is at gate (currentRow === -1) ─── */

function InlineEntrance({ gameSettings = {} }) {
  return (
    <div className="max-w-md mx-auto text-center py-6 sm:py-8 animate-fade-up">
      <div className="bg-gradient-to-b from-[#C84B31]/8 to-transparent rounded-2xl border border-[#C84B31]/20 px-5 sm:px-8 py-6 sm:py-8">
        <span className="text-4xl sm:text-5xl block mb-3">{gameSettings.fm_gate_icon || '🏮'}</span>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">You are in the Souk!</h2>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-xs mx-auto">
          Merchants on your left and right — imagine yourself walking through the alley!
        </p>
        <div className="flex justify-center items-center gap-4 sm:gap-8 mt-5">
          <div className="flex flex-col items-center gap-1 text-gray-400">
            <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
              <span className="text-lg">🏪</span>
            </div>
            <span className="text-[10px] sm:text-xs font-medium">Left shops</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <svg className="w-6 h-6 text-[#2D6A4F] animate-arrow-down" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <span className="text-[10px] sm:text-xs font-bold text-[#2D6A4F]">Walk down</span>
          </div>
          <div className="flex flex-col items-center gap-1 text-gray-400">
            <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
              <span className="text-lg">🏪</span>
            </div>
            <span className="text-[10px] sm:text-xs font-medium">Right shops</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Compact souk banner — always visible when walking ─── */

function SoukBanner({ gameSettings = {} }) {
  return (
    <div className="max-w-md mx-auto text-center py-2">
      <div className="bg-gradient-to-r from-[#C84B31]/5 via-[#C84B31]/10 to-[#C84B31]/5 rounded-xl border border-[#C84B31]/15 px-4 py-2 flex items-center justify-center gap-2">
        <span className="text-base">{gameSettings.fm_gate_icon || '🏮'}</span>
        <span className="text-sm font-bold text-gray-800">You are in the Souk!</span>
        <span className="text-base">{gameSettings.fm_gate_icon || '🏮'}</span>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */

export default function FreeMarketSouk({
  merchants = [], items = [], coins = 0, gameSettings = {},
  playerAvatar, ownedItems = [], merchantFriendship = {},
  wishlist = [],
  onSelectMerchant, onOpenCloset, onDressUp, onBack, playSound
}) {
  const [entered, setEntered] = useState(false);
  const [currentRow, setCurrentRow] = useState(-1);
  const [isWalking, setIsWalking] = useState(false);
  const [callouts, setCallouts] = useState({});
  const [walkDirection, setWalkDirection] = useState(null); // 'up' | 'down' for slide animation
  const rowRefs = useRef([]);
  const entranceRef = useRef(null);
  const navRef = useRef(null); // nav element for camera tracking
  const walkTimerRef = useRef(null);
  const isWalkingRef = useRef(false);
  const scrollAnimRef = useRef(null);

  // Smooth scroll — easeOutCubic like a car braking gently
  const smoothScrollTo = (targetY, duration = 400) => {
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
    const startY = window.scrollY;
    const diff = targetY - startY;
    if (Math.abs(diff) < 5) return;
    const startTime = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      window.scrollTo(0, startY + diff * ease(progress));
      if (progress < 1) {
        scrollAnimRef.current = requestAnimationFrame(step);
      } else {
        scrollAnimRef.current = null;
      }
    };
    scrollAnimRef.current = requestAnimationFrame(step);
  };

  const avatarImgUrl = playerAvatar?.image_url
    ?.replace('?dl=0', '?raw=1')
    .replace('www.dropbox.com', 'dl.dropboxusercontent.com');

  const merchantItemCount = useMemo(() => {
    const counts = {};
    items.forEach(i => {
      if (i.merchant_id && i.is_active !== false) {
        counts[i.merchant_id] = (counts[i.merchant_id] || 0) + 1;
      }
    });
    return counts;
  }, [items]);

  const activeMerchants = useMemo(() => merchants.filter(m => m.is_active !== false), [merchants]);

  const totalActiveItems = useMemo(() => items.filter(i => i.is_active !== false).length, [items]);
  const ownedCount = ownedItems.length;
  const collectionPct = totalActiveItems > 0 ? Math.round((ownedCount / totalActiveItems) * 100) : 0;

  const rows = useMemo(() => {
    const r = [];
    for (let i = 0; i < activeMerchants.length; i += 2) {
      r.push({ left: activeMerchants[i], right: activeMerchants[i + 1] || null });
    }
    return r;
  }, [activeMerchants]);

  const merchantAvatars = useMemo(() =>
    activeMerchants.slice(0, 8).map(m => m.image_url || '🧔'),
    [activeMerchants]
  );

  const phrases = useMemo(() =>
    (gameSettings.fm_callout_phrases?.length > 0)
      ? gameSettings.fm_callout_phrases
      : CALLOUT_PHRASES,
    [gameSettings.fm_callout_phrases]
  );

  const refreshCallouts = useCallback(() => {
    if (rows.length === 0 || !entered || currentRow < 0) return;
    const row = rows[currentRow];
    if (!row) return;
    setCallouts(prev => {
      const next = { ...prev };
      next[row.left.id] = phrases[Math.floor(Math.random() * phrases.length)];
      if (row.right) {
        let rightPhrase = phrases[Math.floor(Math.random() * phrases.length)];
        if (phrases.length > 1 && rightPhrase === next[row.left.id]) {
          rightPhrase = phrases[(phrases.indexOf(rightPhrase) + 1) % phrases.length];
        }
        next[row.right.id] = rightPhrase;
      }
      return next;
    });
  }, [rows, currentRow, phrases, entered]);

  useEffect(() => {
    if (!entered || currentRow < 0) return;
    refreshCallouts();
    const interval = setInterval(refreshCallouts, 4000);
    return () => clearInterval(interval);
  }, [refreshCallouts, entered, currentRow]);

  // Camera follow — scroll to keep nav visible after walk
  useEffect(() => {
    if (!entered || !navRef.current) return;
    const timer = setTimeout(() => {
      if (!navRef.current) return;
      const rect = navRef.current.getBoundingClientRect();
      const viewH = window.innerHeight;
      if (rect.top < 60 || rect.bottom > viewH - 60) {
        const fraction = currentRow === rows.length - 1 ? 0.25 : 0.35;
        const targetY = window.scrollY + rect.top - viewH * fraction;
        smoothScrollTo(Math.max(0, targetY), 400);
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [currentRow, entered, rows.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (walkTimerRef.current) clearTimeout(walkTimerRef.current);
      if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
    };
  }, []);

  const handleEnter = () => {
    setEntered(true);
    setCurrentRow(-1);
    playSound?.('tick');
  };

  // Walk handler — uses isWalkingRef to avoid stale closure bugs
  const handleWalk = (direction) => {
    const next = direction === 'down' ? currentRow + 1 : currentRow - 1;
    if (next < -1 || next >= rows.length || isWalkingRef.current) return;
    isWalkingRef.current = true;
    setIsWalking(true);
    setWalkDirection(direction);
    setCurrentRow(next);
    playSound?.('tick');
    if (walkTimerRef.current) clearTimeout(walkTimerRef.current);
    walkTimerRef.current = setTimeout(() => {
      isWalkingRef.current = false;
      setIsWalking(false);
      walkTimerRef.current = null;
    }, 400);
  };

  // Auto-walk to a row and open merchant's shop
  const walkToRowAndVisit = (rowIdx, merchant) => {
    if (isWalkingRef.current) return;
    if (rowIdx === currentRow) {
      onSelectMerchant(merchant);
      playSound?.('tick');
      return;
    }
    isWalkingRef.current = true;
    setIsWalking(true);
    setWalkDirection(rowIdx > currentRow ? 'down' : 'up');
    setCurrentRow(rowIdx);
    playSound?.('tick');
    if (walkTimerRef.current) clearTimeout(walkTimerRef.current);
    walkTimerRef.current = setTimeout(() => {
      isWalkingRef.current = false;
      setIsWalking(false);
      onSelectMerchant(merchant);
      playSound?.('tick');
      walkTimerRef.current = null;
    }, 500);
  };

  // Jump from souk map strip (no merchant opening)
  const handleMapJump = (targetRow) => {
    if (isWalkingRef.current || targetRow === currentRow) return;
    isWalkingRef.current = true;
    setIsWalking(true);
    setWalkDirection(targetRow > currentRow ? 'down' : 'up');
    setCurrentRow(targetRow);
    playSound?.('tick');
    if (walkTimerRef.current) clearTimeout(walkTimerRef.current);
    walkTimerRef.current = setTimeout(() => {
      isWalkingRef.current = false;
      setIsWalking(false);
      walkTimerRef.current = null;
    }, 500);
  };

  const handleBack = () => {
    if (entered) {
      setEntered(false);
      setCurrentRow(-1);
    } else {
      onBack();
    }
  };

  const isAtFirst = currentRow === -1;
  const isAtLast = currentRow === rows.length - 1;

  return (
    <div className="min-h-screen bg-[#F5F0E8] animate-fade-up">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <button onClick={handleBack} className="text-gray-500 hover:text-gray-800 transition-colors text-sm flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {entered ? 'Exit Souk' : 'Back'}
            </button>
            <div className="flex items-center gap-3">
              {wishlist.length > 0 && (
                <span className="text-sm font-semibold text-pink-500">♡ {wishlist.length}</span>
              )}
              <span className="text-sm font-bold text-amber-700">🪙 {coins}</span>
            </div>
          </div>
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">
              {gameSettings.fm_header_icon || '🏮'} {gameSettings.fm_title || 'Free Market'} {gameSettings.fm_header_icon || '🏮'}
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">{gameSettings.fm_subtitle || 'Walk the souk and shop from any merchant'}</p>
          </div>
        </div>
      </div>

      {/* Souk Map Strip */}
      {entered && rows.length > 0 && (
        <SoukMapStrip
          rows={rows}
          currentRow={currentRow}
          merchantFriendship={merchantFriendship}
          avatarImgUrl={avatarImgUrl}
          playerAvatar={playerAvatar}
          onJumpTo={handleMapJump}
          isWalking={isWalking}
          gameSettings={gameSettings}
        />
      )}

      {/* Collection Progress Bar */}
      {totalActiveItems > 0 && (
        <div className="max-w-2xl mx-auto px-4 pt-3">
          <div className="flex items-center gap-2.5 bg-white/80 rounded-lg px-3 py-2 border border-gray-100">
            <span className="text-sm">📦</span>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[10px] sm:text-xs font-semibold text-gray-600">{ownedCount}/{totalActiveItems} items collected</span>
                <span className="text-[10px] sm:text-xs font-bold text-[#2D6A4F]">{collectionPct}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#2D6A4F] to-[#40916C] rounded-full transition-all duration-500"
                  style={{ width: `${collectionPct}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeMerchants.length === 0 ? (
        <div className="text-center py-16">
          <span className="text-5xl mb-4 block">🏪</span>
          <p className="text-gray-500 text-lg">No merchants available</p>
          <p className="text-gray-400 text-sm mt-1">Check back soon!</p>
        </div>
      ) : !entered ? (
        /* ─── GATE VIEW (before entering) ─── */
        <div className="max-w-2xl mx-auto px-4 pt-4 pb-8">
          <EntranceGate
            merchantCount={activeMerchants.length}
            merchantAvatars={merchantAvatars}
            onEnter={handleEnter}
            gameSettings={gameSettings}
          />
          <div className="mt-6">
            <div className="flex gap-2.5">
              <button onClick={onOpenCloset} className="flex-1 py-3 rounded-xl border-2 border-[#2D6A4F] text-[#2D6A4F] font-bold text-sm sm:text-base hover:bg-[#2D6A4F] hover:text-white transition-colors">
                👗 My Closet
              </button>
              <button onClick={onDressUp} className="flex-1 py-3 rounded-xl border-2 border-[#D4A843] text-[#D4A843] font-bold text-sm sm:text-base hover:bg-[#D4A843] hover:text-white transition-colors">
                👤 Dress Up
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ─── SOUK JOURNEY (after entering) ─── */
        <div className="max-w-2xl mx-auto px-2 sm:px-4 pt-4 pb-8">

          {/* Slide animation keyframes */}
          <style>{`
            @keyframes navSlideDown { from { transform: translateY(-20px); opacity: 0.3; } to { transform: translateY(0); opacity: 1; } }
            @keyframes navSlideUp { from { transform: translateY(20px); opacity: 0.3; } to { transform: translateY(0); opacity: 1; } }
            @keyframes navFadeIn { from { opacity: 0; } to { opacity: 1; } }
          `}</style>

          {/* Entrance area */}
          <div ref={entranceRef}>
            {currentRow === -1 ? (
              <InlineEntrance gameSettings={gameSettings} />
            ) : (
              <SoukBanner gameSettings={gameSettings} />
            )}
          </div>

          {/* Gate stop — nav below entrance when at gate */}
          {currentRow === -1 && (
            <div ref={navRef} className="flex flex-col items-center py-3"
                 style={{ animation: 'navFadeIn 300ms ease-out' }}>
              <div className="w-10 h-10 rounded-full bg-white border-[3px] border-[#D4A843] shadow-lg overflow-hidden flex items-center justify-center">
                {avatarImgUrl ? (
                  <img src={avatarImgUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm">{playerAvatar?.icon || '🧑'}</span>
                )}
              </div>
              <span className="text-[8px] font-bold text-[#2D6A4F] mt-0.5">You</span>
              <ArrowButton direction="down" disabled={isWalking} onClick={() => handleWalk('down')} />
            </div>
          )}

          {/* All merchant rows */}
          {rows.map((row, i) => (
            <React.Fragment key={i}>
              <PathDots active={i <= currentRow} />

              <div
                ref={el => rowRefs.current[i] = el}
                className={`py-2 transition-opacity duration-500 ${
                  i === currentRow ? 'opacity-100' : currentRow === -1 ? 'opacity-30' : 'opacity-50 hover:opacity-70'
                }`}
              >
                <div className="grid grid-cols-[1fr_3.5rem_1fr] sm:grid-cols-[1fr_4rem_1fr] gap-1 sm:gap-2 items-center">
                  <MerchantStall
                    merchant={row.left}
                    callout={i === currentRow ? callouts[row.left.id] : null}
                    itemCount={merchantItemCount[row.left.id] || 0}
                    isLeft={true}
                    index={i * 2}
                    isActive={i === currentRow && !isWalking}
                    friendship={merchantFriendship[row.left.id] || 0}
                    onClick={() => walkToRowAndVisit(i, row.left)}
                    gameSettings={gameSettings}
                  />

                  {/* Center column — nav at current row, dots elsewhere */}
                  <div className="flex flex-col items-center justify-center">
                    {i === currentRow ? (
                      <div
                        ref={navRef}
                        className="flex flex-col items-center"
                        style={{
                          animation: walkDirection
                            ? `${walkDirection === 'down' ? 'navSlideDown' : 'navSlideUp'} 350ms ease-out`
                            : 'navFadeIn 300ms ease-out'
                        }}
                      >
                        <ArrowButton direction="up" disabled={isAtFirst || isWalking} onClick={() => handleWalk('up')} />
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white border-[3px] border-[#D4A843] shadow-lg overflow-hidden flex items-center justify-center">
                          {avatarImgUrl ? (
                            <img src={avatarImgUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs sm:text-sm">{playerAvatar?.icon || '🧑'}</span>
                          )}
                        </div>
                        <span className="text-[7px] sm:text-[8px] font-bold text-[#2D6A4F]">
                          You · {i + 1}/{rows.length}
                        </span>
                        <ArrowButton direction="down" disabled={isAtLast || isWalking} onClick={() => handleWalk('down')} />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 py-3">
                        <div className={`w-1.5 h-1.5 rounded-full ${i <= currentRow ? 'bg-amber-400/40' : 'bg-amber-300/20'}`} />
                        <div className={`w-1 h-1 rounded-full ${i <= currentRow ? 'bg-amber-400/30' : 'bg-amber-400/15'}`} />
                        <div className={`w-1.5 h-1.5 rounded-full ${i <= currentRow ? 'bg-amber-400/40' : 'bg-amber-300/20'}`} />
                      </div>
                    )}
                  </div>

                  {row.right ? (
                    <MerchantStall
                      merchant={row.right}
                      callout={i === currentRow ? callouts[row.right.id] : null}
                      itemCount={merchantItemCount[row.right.id] || 0}
                      isLeft={false}
                      index={i * 2 + 1}
                      isActive={i === currentRow && !isWalking}
                      friendship={merchantFriendship[row.right.id] || 0}
                      onClick={() => walkToRowAndVisit(i, row.right)}
                      gameSettings={gameSettings}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full opacity-20">
                      <span className="text-2xl sm:text-3xl">🏪</span>
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}

          {/* End of souk */}
          {rows.length > 0 && <PathDots active={isAtLast} />}
          <ExitGate gameSettings={gameSettings} />

          {/* Bottom actions */}
          <div className="mt-6">
            <div className="flex gap-2.5">
              <button onClick={onOpenCloset} className="flex-1 py-3 rounded-xl border-2 border-[#2D6A4F] text-[#2D6A4F] font-bold text-sm sm:text-base hover:bg-[#2D6A4F] hover:text-white transition-colors">
                👗 My Closet
              </button>
              <button onClick={onDressUp} className="flex-1 py-3 rounded-xl border-2 border-[#D4A843] text-[#D4A843] font-bold text-sm sm:text-base hover:bg-[#D4A843] hover:text-white transition-colors">
                👤 Dress Up
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No fixed navigation — arrows are part of the avatar unit in the flow */}
    </div>
  );
}
